import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { errorMessage } from '@/api/client'
import { authApi } from '@/api/endpoints'
import { useAuth } from '@/store/auth'
import { useBranding } from '@/lib/useBranding'
import { loadRecaptcha } from '@/lib/recaptcha'

export default function Login() {
  const { user, loading, login } = useAuth()
  const { siteLogo } = useBranding()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  // Google reCAPTCHA, when the server has keys for it: the "I'm not a robot"
  // box, and its answer sent with the sign-in. None configured: as before.
  const [captcha, setCaptcha] = useState(null)        // { enabled, site_key }
  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaError, setCaptchaError] = useState('')
  const captchaBox = useRef(null)
  const captchaWidget = useRef(null)

  useEffect(() => {
    authApi
      .captcha()
      .then(setCaptcha)
      .catch(() => setCaptcha({ enabled: false, site_key: '' }))   // the server decides anyway
  }, [])

  useEffect(() => {
    if (!captcha?.enabled || !captchaBox.current || captchaWidget.current !== null) return
    loadRecaptcha()
      .then((grecaptcha) => {
        if (!captchaBox.current || captchaWidget.current !== null) return
        captchaWidget.current = grecaptcha.render(captchaBox.current, {
          sitekey: captcha.site_key,
          callback: (token) => {
            setCaptchaToken(token)
            setCaptchaError('')
          },
          'expired-callback': () => setCaptchaToken(''),
          'error-callback': () => {
            setCaptchaToken('')
            setCaptchaError('reCAPTCHA could not be checked. Check your connection and try again.')
          },
        })
      })
      .catch(() =>
        setCaptchaError(
          'reCAPTCHA could not load. Check your internet connection and reload the page.',
        ),
      )
  }, [captcha])

  if (!loading && user) return <Navigate to="/" replace />

  const needsCaptcha = Boolean(captcha?.enabled)

  const onSubmit = async (event) => {
    event.preventDefault()
    setError('')
    if (needsCaptcha && !captchaToken) {
      setError("Please tick \u201cI'm not a robot\u201d before signing in.")
      return
    }
    setBusy(true)
    try {
      await login(email, password, needsCaptcha ? captchaToken : undefined)
      navigate('/', { replace: true })
    } catch (err) {
      setError(errorMessage(err))
      // A reCAPTCHA answer is good for one try: a new tick for the next.
      if (needsCaptcha && captchaWidget.current !== null) {
        window.grecaptcha?.reset(captchaWidget.current)
        setCaptchaToken('')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={onSubmit}>
        <div className="login-brand">
          {siteLogo ? (
            <img src={siteLogo} alt="MetroMart" className="login-logo" />
          ) : (
            <span className="login-logo logo-placeholder" aria-hidden="true" />
          )}
          <p>WhatsApp order management</p>
        </div>

        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            autoFocus
          />
        </label>

        <label className="field">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        {needsCaptcha && (
          <div className="login-captcha">
            <div ref={captchaBox} />
            {captchaError && <div className="error-note">⚠️ {captchaError}</div>}
          </div>
        )}

        {error && <div className="error-note">⚠️ {error}</div>}

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={busy || (needsCaptcha && !captchaToken)}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}
